import React, {
  ComponentProps,
  ComponentType,
  FC,
  Fragment,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { Menu, Transition } from '@headlessui/react'
import {
  ChevronDownIcon,
  ClipboardCheckIcon,
  ClipboardCopyIcon,
  DotsVerticalIcon,
} from '@heroicons/react/solid'
import { Button, ButtonSizes, ButtonVariants } from './Button'
import { usePopper } from 'react-popper'
import { Link } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { classNames } from 'ui/utilities'
import { Badge } from './Badge'
import { PlacesType, Tooltip } from 'react-tooltip'
import { cn } from 'utilities/StringUtils'
import { useTranslation } from 'react-i18next'
import { Spinner } from './Spinner'

interface DropdownProps {
  children: ReactNode
  className?: string
  classes?: Partial<{
    button: string
    popper: Partial<{
      body: string
      root: string
    }>
  }>
  buttonLabel?: string
  buttonLoading?: boolean
  buttonHideLabel?: boolean
  buttonVariant?: ButtonVariants
  buttonSize?: ButtonSizes
  buttonLeadingIcon?: (props: React.ComponentProps<'svg'>) => JSX.Element
  buttonChevronHidden?: boolean
  threeDots?: boolean
  aboveModal?: boolean
  avatar?: JSX.Element
  popperOffset?: [number, number]
}

export const Dropdown: FC<DropdownProps> = (props) => {
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: ['bottom-start', 'bottom-end'],
        },
      },
      {
        name: 'offset',
        options: {
          offset: props.popperOffset ?? [0, 0],
        },
      },
    ],
  })

  return (
    <Menu as="div" className={cn('relative inline-block text-left', props.className)}>
      {({ open }) => (
        <>
          {props.threeDots && (
            <Menu.Button
              as="button"
              key={`is_open_${open}`}
              aria-label="Open options"
              className={cn(
                'text-gray-400 hover:text-gray-500 -m-2 p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-violet',
                { 'ring-2 ring-violet': open },
                props.classes?.button
              )}
              onClick={(e) => e.stopPropagation()}
              ref={setReferenceElement}
              role="menu"
              type="button"
            >
              <DotsVerticalIcon className="size-5" aria-hidden />
            </Menu.Button>
          )}

          {props.avatar && (
            <Menu.Button
              as="button"
              key={`is_open_${open}`}
              className={cn(
                'text-gray-400 hover:text-gray-500 -m-2 p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-violet',
                { 'ring-2 ring-violet': open },
                props.classes?.button
              )}
              ref={setReferenceElement}
              role="menu"
              type="button"
            >
              {props.avatar}
            </Menu.Button>
          )}

          {!props.threeDots && !props.avatar && (
            <Menu.Button
              as={Button}
              key={`is_open_${open}`}
              className={props.classes?.button}
              ref={setReferenceElement}
              label={props.buttonLabel}
              hideLabel={props.buttonHideLabel}
              loading={props.buttonLoading}
              leadingIcon={props.buttonLeadingIcon ?? null}
              trailingIcon={!props.buttonChevronHidden ? ChevronDownIcon : null}
              variant={props.buttonVariant ?? 'default'}
              size={props.buttonSize}
            />
          )}

          {createPortal(
            <div
              ref={setPopperElement}
              style={styles.popper}
              className={cn(
                props.aboveModal ? 'z-50' : 'z-20',
                'absolute w-56',
                props.classes?.popper?.root
              )}
              {...attributes.popper}
            >
              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  static
                  className={
                    'mt-2 w-56 rounded-lg shadow-lg bg-background dark:bg-background-muted ring-1 ring-black ring-opacity-5 divide-y divide-border focus:outline-none'
                  }
                >
                  <div className={cn('p-1', props.classes?.popper?.body)}>{props.children}</div>
                </Menu.Items>
              </Transition>
            </div>,
            document.getElementsByTagName('body')[0]
          )}
        </>
      )}
    </Menu>
  )
}

export const DropdownDivider: FC = () => <hr className="my-1 -mx-1 border-t border-border" />

export interface DropdownItemProps extends Pick<ComponentProps<'button'>, 'disabled' | 'onClick'> {
  beta?: boolean
  disabledReason?: string | null
  hideOnClick?: boolean
  href?: ComponentProps<'a'>['href']
  icon?: ComponentType<ComponentProps<'svg'>>
  inAppLink?: boolean
  label?: string
  loading?: boolean
  onMouseEnter?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  onMouseLeave?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  subtitle?: ReactNode
  tooltipPlacement?: PlacesType
  variant?: 'danger' | 'info'
}

export const DropdownItem: FC<DropdownItemProps> = ({
  onClick,
  icon: Icon,
  label,
  href = '',
  disabled = false,
  disabledReason = '',
  tooltipPlacement = 'top',
  subtitle = undefined,
  inAppLink = false,
  hideOnClick = true,
  variant = undefined,
  beta = false,
  loading = false,
  ...props
}) => (
  <Menu.Item disabled={disabled}>
    {({ active }) =>
      onClick || disabled ? (
        <div
          {...(disabled && {
            'data-tooltip-content': disabledReason,
            'data-tooltip-id': 'disabled-dropdown-tooltip',
            'data-tooltip-place': tooltipPlacement,
          })}
        >
          <button
            onClick={(e) => {
              if (!hideOnClick) {
                e.preventDefault()
              }

              onClick?.(e)
            }}
            disabled={disabled || loading}
            className={classNames(
              disabled ? 'bg-gray-50 opacity-75 cursor-not-allowed' : '',
              variant === 'danger' ? 'text-red-500' : '',
              active && !disabled && !variant
                ? 'text-gray-900 bg-gray-100  dark:bg-gray-700 dark:text-gray-300'
                : 'text-gray-700 dark:text-gray-300',
              loading ? 'opacity-50 cursor-wait' : '',
              'group flex items-center p-2 px-3 text-sm rounded-md w-full'
            )}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
          >
            {Icon && loading ? (
              <Spinner margin="mr-3" />
            ) : Icon ? (
              <Icon
                aria-hidden
                className={classNames(
                  'flex-shrink-0 mr-3 h-5 w-5 text-gray-400',
                  variant === 'danger' ? 'text-red-500 group-hover:text-red-500' : '',
                  !disabled && !variant ? 'group-hover:text-gray-500' : ''
                )}
              />
            ) : null}

            <div className="text-left">
              <div>
                <span>{label}</span>
                {beta && <Badge label="Beta" variant="info" />}
                {subtitle !== undefined && (
                  <span className="text-xs text-gray-500 block">{subtitle}</span>
                )}
              </div>
            </div>
          </button>
          {disabled && <Tooltip id="disabled-dropdown-tooltip" />}
        </div>
      ) : inAppLink ? (
        <Link
          to={href}
          title={label}
          className={classNames(
            disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '',
            active && !disabled
              ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
              : 'text-gray-700 dark:text-gray-300',
            'group flex items-center p-2 px-3 text-sm rounded-lg w-full'
          )}
          rel="noreferrer"
        >
          {Icon && (
            <Icon
              aria-hidden
              className={`flex-shrink-0 mr-3 h-5 w-5 text-gray-400 ${
                !disabled ? 'group-hover:text-gray-500' : ''
              }`}
            />
          )}

          <div className="text-left">
            <span>{label}</span>

            {subtitle && <span className="text-xs text-gray-500 block">{subtitle}</span>}
          </div>
        </Link>
      ) : (
        <a
          href={href}
          title={label}
          target="_blank"
          className={classNames(
            disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '',
            active && !disabled
              ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
              : 'text-muted-foreground dark:text-gray-300',
            'group flex items-top p-2 px-3 text-sm rounded-lg w-full'
          )}
          onMouseEnter={props.onMouseEnter}
          onMouseLeave={props.onMouseLeave}
          rel="noreferrer"
        >
          {Icon && (
            <Icon
              aria-hidden
              className={`flex-shrink-0 mr-3 h-5 w-5 text-gray-400 ${
                !disabled ? 'group-hover:text-gray-500' : ''
              }`}
            />
          )}

          <div className="text-left">
            <span>{label}</span>

            {subtitle && <span className="text-xs text-gray-500 block">{subtitle}</span>}
          </div>
        </a>
      )
    }
  </Menu.Item>
)

interface ClipboardDropdownItemProps {
  label: string
  copyableValue: string
  disabled?: boolean
  disabledReason?: string
}

export const ClipboardDropdownItem: FC<ClipboardDropdownItemProps> = (props) => {
  const { t } = useTranslation()
  const [hasCopied, setHasCopied] = useState(false)

  useEffect(() => {
    if (!hasCopied) return

    const timer = setTimeout(() => setHasCopied(false), 2000)

    return () => clearTimeout(timer)
  }, [hasCopied])

  const handleCopy = () => {
    navigator.clipboard.writeText(props.copyableValue)
    setHasCopied(true)
  }

  return (
    <DropdownItem
      label={hasCopied ? t('dropdown.copied') : props.label}
      hideOnClick={false}
      icon={hasCopied ? ClipboardCheckIcon : ClipboardCopyIcon}
      onClick={handleCopy}
      disabled={props.disabled}
      disabledReason={props.disabledReason}
    />
  )
}
